import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Hammer, Spinner, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [throttled, setThrottled] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setThrottled(!!data?.throttled);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-950 text-zinc-100">
      <div className="w-full max-w-sm">
        <Link to="/login" className="flex items-center gap-2.5 mb-8" data-testid="forgot-back-link">
          <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
            <Hammer size={20} weight="bold" className="text-zinc-950" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold">ClickForge</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-400">Lead Agent</div>
          </div>
        </Link>

        {sent ? (
          <div data-testid="forgot-sent-panel" className={`border rounded-sm p-6 text-center space-y-3 ${throttled ? "border-amber-500/30 bg-amber-500/5" : "border-emerald-500/30 bg-emerald-500/5"}`}>
            <CheckCircle size={40} weight="duotone" className={`mx-auto ${throttled ? "text-amber-400" : "text-emerald-400"}`} />
            {throttled ? (
              <>
                <h2 data-testid="forgot-throttled-title" className="font-display text-xl font-semibold text-zinc-50">Too many attempts</h2>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  You've requested a reset link 5 times in the last hour for this address. For security, we've paused new emails.
                  Check your inbox (and spam) for the earlier link, or try again in an hour.
                </p>
              </>
            ) : (
              <>
                <h2 className="font-display text-xl font-semibold text-zinc-50">Check your inbox</h2>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  If an account exists for <span className="text-zinc-200 font-mono text-xs">{email}</span>, we just sent a password reset link. It expires in 24 hours.
                </p>
              </>
            )}
            <Link to="/login" data-testid="forgot-back-to-login" className="inline-block text-orange-400 hover:text-orange-300 text-sm font-medium underline-offset-2 hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-500 mb-2">Forgot password</div>
              <h2 className="font-display text-3xl font-semibold text-zinc-50">Reset your password</h2>
              <p className="text-sm text-zinc-500 mt-1">
                We'll email you a secure link to set a new password.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                  Email
                </Label>
                <Input
                  id="email"
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="mt-1.5 bg-zinc-900 border-zinc-800 focus-visible:border-orange-500 focus-visible:ring-orange-500/30"
                />
              </div>

              {error && (
                <div data-testid="forgot-error" className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-sm px-3 py-2">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                data-testid="forgot-submit-btn"
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 font-medium rounded-sm h-10"
              >
                {loading ? (<><Spinner size={16} className="mr-2 animate-spin" /> Sending…</>) : (<><EnvelopeSimple size={16} className="mr-2" /> Email me a reset link</>)}
              </Button>

              <div className="text-center text-sm text-zinc-500 pt-2">
                Remember it?{" "}
                <Link to="/login" data-testid="forgot-go-login-link" className="text-orange-400 hover:text-orange-300 underline-offset-2 hover:underline">
                  Sign in
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
